import React, { useMemo } from "react";
import { AiOutlineStop } from "react-icons/ai";
import {
  MdCalendarMonth,
  MdHourglassEmpty,
  MdRefresh,
  MdSchool,
} from "react-icons/md";
import Badge from "../DanitComponents/Badges/Badge";
import {
  calculateAverageLessonsPerMonth,
  calculateTeacherRating,
  calculateTeachersChurn,
  tallyTotalLessons,
} from "../utilityFunctions";
import "./admin-teacher-card.scss";
import { format, parse, set } from "date-fns";

const AdminTeacherCard = (props) => {
  const [bkgd, setBkgd] = React.useState(false);
  const [averageLessonsPerMonth, setAverageLessonsPerMonth] = React.useState(0);
  const [churn, setChurn] = React.useState(0);
  const [totalLessons, setTotalLessons] = React.useState(0);
  const [teacherRating, setTeacherRating] = React.useState(0);
  const [fullReason, setFullReason] = React.useState('');

  React.useEffect(() => {
    checkToDisplayFullSign()
    if (props.teacher.BackgroundCheck) {
      props.teacher.BackgroundCheck === "verified"
        ? setBkgd(true)
        : setBkgd(false);
    } else {
      setBkgd(false);
    }
  }, []);

  React.useEffect(() => {
    setAverageLessonsPerMonth(
      calculateAverageLessonsPerMonth(props.teacher.TeacherID, props.timesheets)
    );
    setChurn(calculateTeachersChurn(props.teacher.TeacherID, props.timesheets));
    setTeacherRating(
      calculateTeacherRating(props.teacher.TeacherID, props.timesheets)
    );
    setTotalLessons(
      tallyTotalLessons(props.teacher.TeacherID, props.timesheets)
    );
  }, [props.timesheets]);

  React.useEffect(() => {
    changeBubbleColor(averageLessonsPerMonth, 1, 5);
    changeChurnBubbleColor(churn);
  }, [averageLessonsPerMonth]);

  function changeBubbleColor(numberValue, min, max) {
    // Define the minimum and maximum values for the scale
    const minValue = min;
    const maxValue = max;

    // Calculate the color based on the number value
    let color;
    if (numberValue === maxValue) {
      color = "#00FF00"; // Green color for the best number (4)
    } else if (numberValue >= minValue && numberValue < maxValue) {
      const red = Math.round(((maxValue - numberValue) * 255) / 3); // Gradually decrease the red value
      const green = Math.round(((numberValue - minValue) * 220) / 3); // Gradually increase the green value
      color = `rgb(${red}, ${green}, 0)`; // Combine red and green values
    } else {
      color = "#FF0000"; // Red color for the warning number (1)
    }

    // Set the bubble's color
    const bubble = document.getElementById(
      `average-lessons-per-month-${props.teacher.TeacherID}`
    ); // Replace 'bubble' with the ID of your bubble element
    if (bubble) {
      bubble.style.backgroundColor = color;
    }
  }

  function changeChurnBubbleColor(numberValue) {
    let color;
    if (numberValue <= 50) {
      const green = Math.round((numberValue / 55) * 220); // Gradually increase the green value
      const red = 220 - green; // Decrease the red value based on the green value
      color = `rgb(${green}, ${red}, 0)`; // Combine red and green values
    } else {
      color = "rgb(255, 0, 0)"; // Red color for any number over 50
    }

    // Set the bubble's color
    const bubble = document.getElementById(
      `churn-bubble-${props.teacher.TeacherID}`
    ); // Replace 'bubble' with the ID of your bubble element
    if (bubble) {
      bubble.style.backgroundColor = color;
    }
  }

  const checkToDisplayFullSign = () => {
    //IF TEACHER IS LEAVING / LEFT / FULL / PROBATION
    if (props.teacher.Status === "leaving") {
      setFullReason(props.teacher.DepartureDate);
      return true;
    } else if (props.teacher.Status === "Left School") {
      setFullReason("Left School");
      return true;
    } else if (!props.teacher.WantsMoreStudents) {
      setFullReason("Full");
      return true;
    } else if (props.teacher.Status === "goon") {
      setFullReason("Probation");
      return true;
    } else {
      return false;
    }
  }

  const instrumentDisplayLength = 2;
  const instruments = props.teacher.InstrumentList;
  const instrumentBadgeProps = { color: "neutral", size: "sm" };
  const teacher = props.teacher;


  const badgeStatus = useMemo(() => {
    if (teacher.Status === "Active" && (teacher.newStudentList && teacher.newStudentList.length > 0)) {
      return {
        color: "success",
        label: "Active",
      };
    } else if (teacher.Status === "Active" && (teacher.newStudentList && teacher.newStudentList.length === 0)) {
      return {
        color: "warning",
        label: "Waiting",
      };
    } else if (teacher.Status === "leaving") {
      return {
        color: "danger",
        label: "Leaving",
      };
    } else if (teacher.Status === "goon") {
      return {
        color: "danger",
        label: "Probation",
      };
    } else if (teacher.Status === "Left School") {
      return {
        color: "danger",
        label: "Left School",
      };
    } else return null;
  }, [teacher.Status, teacher.newStudentList]);

  const detailItems = useMemo(
    () => [
      {
        id: "lessonsPerMonth",
        icon: <MdCalendarMonth />,
        label: `${averageLessonsPerMonth || 0} Lessons / Month`,
      },
      {
        id: "students",
        icon: <MdSchool />,
        label: `${teacher.newStudentList ? teacher.newStudentList.length : 0} Active Students`,
      },
      {
        id: "lessonsTaught",
        icon: <MdHourglassEmpty />,
        label: `${totalLessons || 0} Lessons Taught`,
      },
      // {
      //   id: "turnoverRate",
      //   icon: <MdRefresh />,
      //   label: `${churn}% Turnover Rate`,
      // },
    ],
    [
      averageLessonsPerMonth,
      churn,
      teacher.newStudentList,
      totalLessons,
    ]
  );

  const getShortName = (parentInstrument) => {
    const nameArray = parentInstrument.split(" ");
    return nameArray.length === 2
      ? `${nameArray[0][0]}. ${nameArray[1]}`
      : parentInstrument;
  };
  const displayedParentInstruments = new Set();

  if (props.teacher.School !== "admin") {
    return (
      <div
        className="admin-teacher-card"
        onClick={() => props.openModal(props.teacher)}
      >
        <div
          className="admin-teacher-card-pic"
          style={{
            backgroundImage: `url(https://app.atharo.app/images/${teacher.ImageName})`,
          }}
        >
          {fullReason !== '' && (
            <div className="admin-teacher-card-unavailable">
              <AiOutlineStop size={25}/>
              <div className="admin-teacher-card-unavailable-text">
                {fullReason}
              </div>
            </div>
          )}
          {badgeStatus && (
            <div className="admin-teacher-card-status">
              <Badge {...badgeStatus} size={"sm"} />
            </div>
          )}

          <div className="admin-teacher-card-pic-overlay justify-center items-center pt-3">
            <div className="flex flex-col gap-3">
              {detailItems.map((item) => (
                <div className="flex gap-2" key={item.id}>
                  {item.icon}{" "}
                  <div className="text-sm text-slate-600">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="admin-teacher-card-name truncate w-full px-10">
          {teacher.Name}
        </div>
        <div className="flex items-center justify-evenly w-full mt-2">
        <>
      {instruments.slice(0, instrumentDisplayLength).map((instrument) => {
        const { parentInstrument, id } = instrument;
        const shortName = getShortName(parentInstrument);

        if (!displayedParentInstruments.has(parentInstrument)) {
          // If this parentInstrument has not been displayed yet, display the badge
          displayedParentInstruments.add(parentInstrument);
          return (
            <Badge
              {...instrumentBadgeProps}
              label={shortName}
              key={id}
            />
          );
        } else {
          return null; // If this parentInstrument has already been displayed, skip rendering the badge
        }
      })}
    </>
          {instruments.length > instrumentDisplayLength && (
            <Badge
              {...instrumentBadgeProps}
              label={`+${instruments.length - instrumentDisplayLength}`}
            />
          )}
        </div>
      </div>
    );
    // (
    //     <div className="admin-teacher-card" id={`admin-teacher-card-${props.teacher.TeacherID}`} onClick={() => props.openModal(props.teacher)}>
    //         {props.teacherStatus === 'goon' &&
    //             <img className='goon-mode__drake' src='./drake.jpg' />
    //         }
    //         {props.teacherStatus === 'leaving' &&
    //             <div className='leaving-mode__label'>
    //                 <div>LEAVING</div>
    //                 {isValid(new Date(parseISO(props.teacher.DepartureDate))) && <div>{format(new Date(parseISO(props.teacher.DepartureDate)), 'MMMM dd, yyyy')}</div>}
    //             </div>
    //         }
    //         {
    //             props.teacherStatus === 'Left School' || props.teacherStatus === 'goon' || props.teacherStatus === 'leaving' ?
    //             <div className="admin-teacher-card-pic" style={{opacity: 0.3, backgroundImage: 'url(https://app.atharo.app/images/' + props.teacher.ImageName + ')'}}></div>
    //             :
    //             <div className="admin-teacher-card-pic" style={{opacity: 1, backgroundImage: 'url(https://app.atharo.app/images/' + props.teacher.ImageName + ')'}}></div>
    //         }
    //         {props.teacher.newStudentList && props.teacher.newStudentList.length > 0 ? <div className="student-number-bubble">{props.teacher.newStudentList.length}</div> : null}
    //         {!isNaN(averageLessonsPerMonth) && <div className='total-lessons-bubble'>{totalLessons} TLT</div>}
    //         {!isNaN(averageLessonsPerMonth) && <div className='average-lessons-bubble' id={`average-lessons-per-month-${props.teacher.TeacherID}`}>{averageLessonsPerMonth} LPM</div>}
    //         {!isNaN(averageLessonsPerMonth) && <div className='churn-bubble' id={`churn-bubble-${props.teacher.TeacherID}`}>{churn}% CRN</div>}

    //         {/* {!isNaN(averageLessonsPerMonth) && <div className='churn-bubble' id={`churn-bubble-${props.teacher.TeacherID}`}>{teacherRating}</div>} */}
    //         { !props.teacher.WantsMoreStudents &&
    //             <div className="no-more-students__hand">
    //                 <img src="stop-hand.svg"/>
    //             </div>
    //         }
    //         { props.teacher.BackgroundCheck === 'verified' &&
    //             <div className="background-check-checkmark" style={{backgroundColor: "green"}}>
    //                 <img className="background-check-icon" src="check.svg"/>
    //             </div>
    //         }
    //         { props.teacher.BackgroundCheck === 'ordered' &&
    //             <div className="background-check-checkmark" style={{backgroundColor: "yellow"}}>
    //                 <img className="background-check-icon" src="phone.svg"/>
    //             </div>
    //         }
    //         { props.teacher.BackgroundCheck === 'pending' &&
    //             <div className="background-check-checkmark" style={{backgroundColor: "rgb(182, 118, 0)"}}>
    //                 <img className="background-check-icon" src="question.svg"/>
    //             </div>
    //         }
    //         <div className="admin-teacher-card-name">{props.teacher.Name}</div>
    //     </div>
    // )
  } else {
    return null;
  }
};
export default AdminTeacherCard;
